<template>
  <div class="h-100 d-flex flex-column" :style="mainComponentStyle">
    <a-card
      id="mark-objects-card"
      class="h-100 d-flex flex-column"
      size="small"
      :body-style="cardBodyStyle"
      :loading="loadingImages || isBulkDelete"
    >
      <div v-if="imageSpinner" class="spinner-container">
        <a-spin size="large" :spinning="imageSpinner" tip="Loading Images..." />
      </div>

      <!-- selectedImage -->
      <DrawObjects
        v-if="selectedImage"
        ref="drawObjects"
        class="draw-objects-card"
        :image-path-in-bucket="requestedImagePath"
        :image-list="imagesForAnnotation"
        :image-id="requestedImageId"
        :image-obj="selectedImage"
        :image-index="currentImageIndex"
        :total-results="totalImagesForAnnotation"
        :current-page="currentPageForImage"
        :page-size="pageSizeForImage"
        :start-index="startIndexCache"
        :end-index="endIndexCache"
        :presigned-url-list="presignedUrlList"
        :annotation-filter-values="annotationFilterValues"
        :sorting-value="sortingValue"
        :reload-presigned-url="reloadPresignedUrl"
        :model="model"
        :mask-data="preserveMaskData"
        :enable-fast-annotation="fastAnnotation"
        @closeModal="selectedImage = null"
        @nextImage="handleImageChange"
        @prevImage="handleImageChange"
        @updateAnnotationPath="updateAnnotationPath"
        @updateCacheIndex="updateCacheIndex"
        @preserve-mask-data-input="preserveMaskDataInput"
        @set-fast-annotation="setFastAnnotation"
      />
      <template #title>
        <div class="d-flex align-items-center">
          <a-button
            v-if="selectedImage"
            class="text-dark"
            type="link"
            @click="selectedImage = null"
          >
            <template #icon>
              <arrow-left-outlined />
            </template>
          </a-button>

          <template v-if="!selectedImage">
            <a-checkbox
              v-model:checked="selectAllImages"
              class="m-1"
              :disabled="
                imageFileList.length === 0 || loadingImages || isBulkDelete
              "
              @change="handleSelectAllFiles"
            />

            <div class="d-flex align-items-center">
              <a-typography-title :level="5" class="my-0 ml-3">
                Images: &nbsp;
                <a-tag id="annotation-objects-count" color="blue">
                  {{ totalImagesForAnnotation }}
                </a-tag>
              </a-typography-title>

              <a-typography-title
                v-if="getSelectedImagesCount"
                :level="5"
                class="my-0 ml-3"
              >
                Selected Images: &nbsp;
                <a-tag id="annotation-objects-count" color="blue">
                  {{ getSelectedImagesCount }}
                </a-tag>
              </a-typography-title>

              <a-popover placement="bottom">
                <template #content>
                  <div class="popover-content">
                    <a-tag :color="colorPalette.green">
                      Annotated: {{ getAnnotatedImagesCount }}
                    </a-tag>
                    <a-tag :color="colorPalette.red">
                      Not Annotated: {{ getNonAnnotatedImagesCount }}
                    </a-tag>
                    <a-tag :color="colorPalette.yellow">
                      Empty Annotated: {{ getEmptyAnnotatedImagesCount }}
                    </a-tag>
                    <a-tag color="#e3a805">
                      Review Required: {{ getReviewRequiredImagesCount }}
                    </a-tag>
                    <a-tag :color="colorPalette.orange">
                      AI Annotated: {{ getAIAnnotatedImagesCount }}
                    </a-tag>
                    <a-tag :color="colorPalette.orange">
                      Propogation Review: {{ getPropogationReviewImagesCount }}
                    </a-tag>
                  </div>
                </template>

                <a-tooltip>
                  <a-button id="counts" type="default" size="small">
                    <template #icon>
                      <InfoCircleOutlined />
                    </template>
                  </a-button>
                </a-tooltip>
              </a-popover>
            </div>

            <div class="w-100 d-flex justify-content-center">
              <a-input
                v-model:value="searchImage"
                placeholder="Search Image"
                class="w-50"
                :disabled="loadingImages || isBulkDelete"
                @input="debouncedSearch"
                @keyup.enter="debouncedSearch"
              >
                <template #suffix>
                  <search-outlined style="color: rgba(0, 0, 0, 0.45)" />
                </template>
              </a-input>
            </div>
          </template>

          <a-typography-title v-else :level="5" class="mx-auto">
            {{ getFileName(selectedImage.path_to_image) }}
          </a-typography-title>
        </div>
      </template>

      <template v-if="!selectedImage" #extra>
        <a-space>
          <a-tooltip title="Filter">
            <a-select
              v-model:value="annotationFilterValues"
              :options="annotationFilterOptions"
              mode="multiple"
              size="small"
              placeholder="Apply filter"
              style="width: 200px"
              @change="handleGetImagesForAnnotation"
              @popupScroll="popupScroll"
            ></a-select>
          </a-tooltip>
          <a-popover placement="bottom">
            <template #content>
              <a-radio-group
                v-model:value="sortingValue"
                style="display: flex; flex-direction: column"
              >
                <a-radio
                  value="name"
                  @click="() => handleSortImagesForAnnotation('name')"
                >
                  Name
                </a-radio>
                <a-radio
                  value="id"
                  @click="() => handleSortImagesForAnnotation('id')"
                >
                  ID
                </a-radio>
              </a-radio-group>
            </template>
            <template #title>
              <span>Sort By</span>
            </template>
            <a-tooltip title="Sort based on Name or ID">
              <a-button id="refresh-images-btn" type="default" size="small">
                <template #icon>
                  <sort-ascending-outlined />
                </template>
              </a-button>
            </a-tooltip>
          </a-popover>

          <a-tooltip title="Reload Images">
            <a-button
              id="refresh-images-btn"
              type="default"
              size="small"
              @click="reloadImages"
            >
              <template #icon>
                <reload-outlined />
              </template>
            </a-button>
          </a-tooltip>

          <a-tooltip title="Change View">
            <a-button
              id="change-view-btn"
              type="default"
              size="small"
              @click="handleChangeView"
            >
              <template #icon>
                <FolderViewOutlined />
              </template>
            </a-button>
          </a-tooltip>
          <template v-if="!isImageSelected && segmentId">
            <a-tooltip title="Segment Propagate">
              <a-button
                id="segment-propagate-btn"
                type="default"
                size="small"
                @click="handleSegmentPropagation"
              >
                <template #icon>
                  <FormatPainterOutlined />
                </template>
              </a-button>
            </a-tooltip>
          </template>
          <template v-if="!isImageSelected">
            <a-tooltip title="AI Annotation">
              <a-button
                id="ai-annotation-btn"
                type="default"
                size="small"
                @click="handleAiAnnotation"
              >
                <template #icon>
                  <ScanOutlined />
                </template>
              </a-button>
            </a-tooltip>
          </template>

          <template v-if="!isImageSelected">
            <a-tooltip title="Revert Objects">
              <a-button
                id="ai-annotation-btn"
                type="default"
                size="small"
                @click="handleShowRevertObject"
              >
                <template #icon>
                  <RollbackOutlined />
                </template>
              </a-button>
            </a-tooltip>
          </template>

          <template v-if="!isImageSelected">
            <a-popconfirm
              title="Are you sure you want to delete selected records?"
              ok-text="Yes"
              cancel-text="No"
              @confirm="handleDelete"
            >
              <a-tooltip title="Delete Records">
                <a-button
                  id="delete-all-btn"
                  type="danger"
                  size="small"
                  :disabled="isBulkDelete || loadingImages"
                >
                  <template #icon>
                    <DeleteOutlined />
                  </template>
                </a-button>
              </a-tooltip>
            </a-popconfirm>
          </template>

          <template v-if="!isImageSelected">
            <a-popconfirm
              title="Are you sure you want to delete selected annotations?"
              ok-text="Yes"
              cancel-text="No"
              @confirm="handleDeleteAnnotations"
            >
              <a-tooltip title="Delete Annotations">
                <a-button
                  id="delete-btn"
                  type="default"
                  size="small"
                  :disabled="isBulkDelete || loadingImages"
                >
                  <template #icon>
                    <CloseOutlined />
                  </template>
                </a-button>
              </a-tooltip>
            </a-popconfirm>
          </template>
          <template v-if="!isImageSelected">
            <a-popconfirm
              title="Are you sure you want to mark these images as reviewed?"
              ok-text="Yes"
              cancel-text="No"
              @confirm="markImagesAsReviewed"
            >
              <a-tooltip title="Mark Reviewed">
                <a-button type="default" size="small" :disabled="loadingImages">
                  <template #icon>
                    <CheckOutlined />
                  </template>
                </a-button>
              </a-tooltip>
            </a-popconfirm>
          </template>

          <a-tooltip title="AI Annotation Results">
            <a-button
              id="ai-annotation-result-btn"
              type="default"
              size="small"
              @click="handleAiAnnotationResultsClick"
            >
              <template #icon>
                <file-done-outlined />
              </template>
            </a-button>
          </a-tooltip>

          <a-tooltip v-if="userRole" title="Upload Image File">
            <a-button
              id="upload-images-btn"
              type="default"
              size="small"
              @click="fileUpload = true"
            >
              <template #icon>
                <UploadOutlined />
              </template>
            </a-button>
          </a-tooltip>
          <a-switch
            v-model:checked="fastAnnotation"
            checked-children="Fast Annotation"
            un-checked-children="Fast Annotation"
          />
        </a-space>
      </template>

      <template #actions>
        <a-pagination
          v-if="!selectedImage && !isBulkDelete"
          id="object-detection-pagination"
          v-model:current="currentPageForImage"
          v-model:pageSize="pageSizeForImage"
          :total="totalImagesCount"
          :show-total="
            (total, range) => `${range[0]}-${range[1]} of ${total} Frames`
          "
          class="w-100 d-flex justify-content-center"
          :page-size-options="[30, 50, 100, 150]"
          @change="handlePageChange"
          @show-size-change="handlePageSizeChange"
        />
      </template>
      <a-list
        v-if="!selectedImage"
        :id="!listView ? 'label-objects-list' : 'label-objects-list-view'"
        item-layout="horizontal"
        :grid="
          listView
            ? null
            : {
                gutter: 32,
                xs: 1,
                sm: 1,
                md: 2,
                lg: 3,
                xl: 4,
                xxl: 4,
                xxxl: 5,
              }
        "
        :data-source="imageSpinner ? [] : imageFileList"
        class="list-pagination-display"
        :pagination="{
          pageSize: pageSizeForImage,
          current: currentPageForImage,
          total: totalImagesCount,
        }"
      >
        <template #renderItem="{ item, index }">
          <a-list-item
            v-if="listView"
            :key="item.id"
            class="cursor-pointer file-item"
            @dblclick="openAnnotationModal(item)"
            @click="updateSelectItem(item)"
          >
            <template #actions>
              <a-tag
                v-if="item.id == segmentId"
                :key="'list-edit-segment-' + item.id"
                color="cyan"
              >
                Segmented
              </a-tag>
              <a-tag
                v-if="staticImage[item.id]"
                :key="'list-edit-static-' + item.id"
                color="cyan"
              >
                Static
              </a-tag>
              <a-tag
                v-if="
                  item.path_to_annotations &&
                  !isObjectEmpty(item.task_object) &&
                  !item.ai_annotation
                "
                :key="'list-edit-' + item.id"
                :color="colorPalette.green"
              >
                Annotated
              </a-tag>
              <a-tag
                v-if="
                  item.path_to_annotations && isObjectEmpty(item.task_object)
                "
                :key="'list-edit-' + item.id"
                :color="colorPalette.yellow"
              >
                Empty Annotated
              </a-tag>
              <a-tag
                v-if="!item.path_to_annotations"
                :key="'list-edit-' + item.id"
                :color="colorPalette.red"
              >
                Not Annotated
              </a-tag>
              <a-tag
                v-if="item.requires_review"
                :key="'list-edit-review-' + item.id"
                color="#e3a805"
                >AI Review Required
              </a-tag>
              <a-tag
                v-if="item.ai_annotation"
                :key="'list-edit-' + item.id"
                :color="colorPalette.orange"
              >
                AI Annotated
              </a-tag>
              <a-tag
                v-if="item.requires_propagation_review"
                :key="'list-edit-' + item.id"
                :color="colorPalette.orange"
                >Propagation Review Required
              </a-tag>
            </template>
            <div>
              <a-checkbox
                v-model:checked="selectImage[item.id]"
                class="m-3"
                @change="populateMetaFile(item)"
                @click.stop
              />
              <a-typography-title
                :level="4"
                class="ml-1"
                style="display: inline"
              >
                {{ getFileName(item.path_to_image) }}
              </a-typography-title>
            </div>
          </a-list-item>
          <template v-else>
            <nested-badge-wrapper
              :key="item.id"
              :item="item"
              :static-image="staticImage"
              :segment-id="segmentId"
              :presigned-url-list="presignedUrlList"
              :select-image="selectImage"
              :get-file-name="getFileName"
              :open-annotation-modal="openAnnotationModal"
              :delete-annotation="deleteAnnotation"
              :get-ribbon-color="getRibbonColor"
              :get-ribbon-text="getRibbonText"
              :get-imaget-item-index="getImagetItemIndex"
              @update-select-all="updateSelectAll"
            />
          </template>
        </template>
      </a-list>
    </a-card>
  </div>
  <a-modal
    :visible="visible"
    title="Total Labeled Objects"
    :footer="null"
    :body-style="{ maxHeight: '400px', overflow: 'auto' }"
    class="p-1 pb-3"
    @cancel="visible = false"
  >
    <a-list
      size="small"
      :data-source="[...annotatedObjectList]"
      :loading="annotatedObjectList.length == 0"
    >
      <template #renderItem="{ item }">
        <a-list-item v-if="!item.is_static">
          <div>
            {{ item.name }}
          </div>
          <div>
            {{ item.count }}
          </div>
        </a-list-item>
      </template>
    </a-list>
  </a-modal>

  <a-modal
    :visible="fileUpload"
    title="Upload Image File"
    centered
    @cancel="handleCancelUploadModal"
  >
    <template #footer>
      <a-button
        type="primary"
        :disabled="fileList.length === 0"
        :loading="isUploadingFile"
        @click="handleUploadImageFile"
      >
        {{
          isUploadingFile
            ? fileUploadProgress > 0 && fileUploadProgress < 100
              ? 'Extracting'
              : uploadingZipDetails >= 100
              ? 'Extraction begin'
              : 'Uploading'
            : fileUploadProgress === 100
            ? 'Close'
            : 'Upload'
        }}
      </a-button>
    </template>

    <a-upload
      v-model:file-list="fileList"
      class="w-100"
      :disabled="isUploadingFile"
      accept=".zip"
      name="file"
      :max-count="1"
      :before-upload="(f) => false"
      @change="handleUploadFile"
    >
      <a-space :size="15">
        <a-space>
          <a-button :disabled="isUploadingFile">
            <upload-outlined></upload-outlined>
            Click to Upload Zip File
          </a-button>
          <span style="cursor: default" @click.stop>File Limit: 1 GB</span>
        </a-space>
        <div @click.stop>
          <a-checkbox v-model:checked="isAnnotationFiles">
            Annotation Files
          </a-checkbox>
        </div>
      </a-space>
    </a-upload>

    <div v-if="uploadingZipDetails || fileUploadProgress" class="mt-3">
      <template v-if="fileUploadProgress <= 0">
        <a-typography-text v-if="uploadingZipDetails < 100" strong>
          Uploading zip file in progress...
        </a-typography-text>
        <a-typography-text v-else strong>
          Zip file uploaded. Please wait for extraction...
        </a-typography-text>
      </template>
      <a-typography-text v-else strong>
        <template v-if="fileUploadStatus === activeStatus">
          Unzipping files in progress...
        </template>
        <template v-else> Files unzip progress completed. </template>
      </a-typography-text>

      <a-progress
        v-if="uploadingZipDetails < 100"
        :percent="uploadingZipDetails || fileUploadProgress"
        :status="fileUploadStatus"
      />
      <div v-if="failedFiles.length" class="mt-3">
        <a-typography-text strong>
          Following Files failed to upload:
        </a-typography-text>
        <a-list
          size="small"
          bordered
          :data-source="failedFiles"
          style="max-width: 100%; height: 15vh; overflow-y: auto"
        >
          <template #renderItem="{ item }">
            <a-list-item style="color: red">
              {{ item }}
            </a-list-item>
          </template>
        </a-list>
      </div>
    </div>
  </a-modal>

  <auto-annotation-modal
    :show-ai-annotation="showAiAnnotation"
    :selected-images="selectImage"
    :task-objects-list="taskObjectsList"
    @set-show-ai-annotation="setShowAiAnnotation"
  />
  <revert-object-modal
    :show-revert-object="showRevertObjects"
    :selected-images="selectImage"
    :task-objects-list="taskObjectsList"
    @set-show-revert-object="setShowRevertObjects"
    @revert-objects-process="revertObjectsProcess"
  />

  <a-modal
    id="ai-annotation-results"
    v-model:visible="showAiAnnotationResults"
    title="AI Annotation Results"
    centered
    destroy-on-close
    style="width: 70%"
    :mask-closable="false"
  >
    <template #footer>
      <a-button
        key="close-btn"
        type="primary"
        @click="setShowAiAnnotationResults(false)"
      >
        Close
      </a-button>
    </template>
    <AIAnnotationResults />
  </a-modal>

  <OperationProgress
    v-if="annotationProgress.length"
    :process-list="annotationProgress"
    :process-type="progressProcessType"
  />
</template>
<script>
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  FileDoneOutlined,
  FolderViewOutlined,
  FormatPainterOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  RollbackOutlined,
  ScanOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  UploadOutlined,
} from '@ant-design/icons-vue';
import axios from 'axios';
import { debounce } from 'lodash';
import * as ort from 'onnxruntime-web';
import OperationProgress from 'src/components/shared/Components/OperationProgress.vue';
import UploadFileHelper from 'src/components/shared/Helpers/uploadFile';
import { colorPalette } from 'src/config/color-config';
import { roles } from 'src/config/roles-config';
import LRTService from 'src/services/longRunningTask';
import SQSServices from 'src/services/sqs';
import TaskObjectService from 'src/services/taskObjectsMarking';
import TaskService from 'src/services/tasks';
import { mapActions, mapGetters } from 'vuex';
import httpClient from '../../../../../service/httpClient';
import { parseStringCoordsToObj } from '../../LabelData/ObjectAnnotation/helpers';
import AutoAnnotationModal from './AIAnnotationModal.vue';
import AIAnnotationResults from './AIAnnotationResults.vue';
import DrawObjects from './DrawObjects.vue';
import NestedBadgeWrapper from './NestedBadgeWrapper.vue';
import PreviewImageCard from './PreviewImageCard.vue';
import RevertObjectModal from './RevertObjectModal.vue';
import { longRunningTaskStatus, ODQueues } from './config';
export default {
  name: 'AnnotationList',
  components: {
    OperationProgress,
    ReloadOutlined,
    SortAscendingOutlined,
    ArrowLeftOutlined,
    UploadOutlined,
    FolderViewOutlined,
    DeleteOutlined,
    SearchOutlined,
    DrawObjects,
    NestedBadgeWrapper,
    PreviewImageCard,
    ScanOutlined,
    RollbackOutlined,
    AutoAnnotationModal,
    RevertObjectModal,
    FileDoneOutlined,
    AIAnnotationResults,
    InfoCircleOutlined,
    CloseOutlined,
    CheckOutlined,
    FormatPainterOutlined,
  },
  inject: ['toast'],

  data() {
    return {
      isAnnotationFiles: false,
      isUploadingFile: false,
      fileUpload: false,
      fileList: [],
      annotatedObjectList: [],
      visible: false,
      openObjectAnnotateDialog: false,
      requestedImagePath: '',
      requestedImageId: '',
      user: localStorage.getItem('id'),
      currentPageForImage: 1,
      pageSizeForImage: 30,
      currentImageIndex: -1,
      selectedImage: null,
      presignedUrlList: {},
      metaFiles: [],
      metaFileName: {},
      imageSpinner: false,
      loading: true,
      polling: null,
      isPolling: false,
      userRole: false,
      timeout: null,
      startIndexCache: 0,
      endIndexCache: 30,
      listView: true,
      isBulkDelete: false,
      selectImage: {},
      selectAllImages: false,
      searchImage: '',
      searchDebounce: '',
      imageFileList: [],
      totalImagesCount: 0,
      longRunningTaskId: null,
      fileUploadProgress: 0,
      fileUploadStatus: 'active',
      activeStatus: 'active',
      failedFiles: [],
      assignImageUrl: false,
      markStatic: false,
      markStaticImageId: null,
      staticImage: {},
      uploadingZipDetails: 0,
      annotationFilterOptions: [
        { label: 'Annotated', value: '1' },
        { label: 'Not Annotated', value: '2' },
        { label: 'Empty Annotation', value: '3' },
        { label: 'AI Requires Review', value: '4' },
        { label: 'AI Annotated', value: '5' },
        { label: 'Propagation Review', value: '6' },
      ],
      annotationFilterValues: [],
      sortingValue: 'name',
      colorPalette: colorPalette,
      showAiAnnotation: false,
      showRevertObjects: false,
      taskObjectsList: [],
      selectedAnnotation: [],
      interval: null,
      currentAiAnnotationSet: new Set(),
      showAiAnnotationResults: false,
      reloadPresignedUrl: false,
      annotationProgress: [],
      segmentId: null,
      progressProcessType: '',
      model: null,
      fastAnnotation: false,
      preserveMaskData: {
        selectedAnnotation: null,
        mask: [],
      },
    };
  },

  computed: {
    ...mapGetters([
      'organization',
      'taskName',
      'selectedTask',
      'taskObjects',
      'taskRegionMarkingImgUrl',
      'loadingImages',
      'imagesForAnnotation',
      'totalImagesForAnnotation',
      'taskStaticAnnotationImage',
      'taskStaticObjectsList',
    ]),

    isImageSelected() {
      for (const [key, value] of Object.entries(this.selectImage)) {
        if (value) {
          return false;
        }
      }
      this.selectAllImages = false;
      return true;
    },

    getSelectedImagesCount() {
      let count = 0;
      for (const [key, value] of Object.entries(this.selectImage)) {
        if (value) {
          count += 1;
        }
      }
      return count;
    },

    getAnnotatedImagesCount() {
      let count = 0;
      this.imageFileList.forEach((imageFile) => {
        if (
          !this.isObjectEmpty(imageFile.task_object) &&
          imageFile.path_to_annotations !== null
        ) {
          count++;
        }
      });
      return count;
    },

    getNonAnnotatedImagesCount() {
      let count = 0;
      this.imageFileList.forEach((imageFile) => {
        if (imageFile.path_to_annotations == null) {
          count++;
        }
      });
      return count;
    },

    getEmptyAnnotatedImagesCount() {
      let count = 0;
      this.imageFileList.forEach((imageFile) => {
        if (
          this.isObjectEmpty(imageFile.task_object) &&
          imageFile.path_to_annotations != null
        ) {
          count++;
        }
      });
      return count;
    },
    getAIAnnotatedImagesCount() {
      let count = 0;
      this.imageFileList.forEach((imageFile) => {
        if (imageFile.ai_annotation) {
          count++;
        }
      });
      return count;
    },
    getPropogationReviewImagesCount() {
      let count = 0;
      this.imageFileList.forEach((imageFile) => {
        if (imageFile.requires_propagation_review) {
          count++;
        }
      });
      return count;
    },

    getReviewRequiredImagesCount() {
      let count = 0;
      this.imageFileList.forEach((imageFile) => {
        if (imageFile.requires_review === true) {
          count++;
        }
      });
      return count;
    },

    cardBodyStyle() {
      return !this.selectedImage
        ? {
            flexGrow: '1',
            height: '1px',
            overflowY: 'auto',
            overflowX: 'hidden',
          }
        : { height: '100%' };
    },

    mainComponentStyle() {
      let style = {
        margin: 'auto',
        width: '95%',
      };

      return !this.selectedImage ? style : {};
    },
  },

  watch: {
    visible: async function (value) {
      if (!value) return;
      this.annotatedObjectList = [];
      await this.getAnnotatedObjectCount();
    },

    async selectedImage(newValue) {
      if (newValue == null) {
        this.presignedUrlList = {};
        await this.handleGetImagesForAnnotation();
      }
    },

    setTaskObject(value) {
      this.taskObjectsList = value;
    },

    imageFileList(value) {
      if (value.length === 0) {
        this.selectAllImages = false;
        this.selectImage = {};
      } else {
        let set = new Set();
        this.segmentId = this.getSegmentPropagationImageId();
        for (let image of value) {
          set.add(`${image.id}`);
          this.staticImage[image.id] =
            image.id == this.taskStaticAnnotationImage;
        }
        Object.keys(this.selectImage).forEach((key) => {
          if (!set.has(`${key}`)) this.selectImage[key] = false;
        });
        if (this.selectAllImages) {
          for (let image of value) {
            this.selectImage[`${image.id}`] = true;
          }
        }
      }
    },

    selectAllImages(value) {
      if (!value) this.selectImage = {};
    },
  },

  async mounted() {
    this.getActiveUserRole();
    this.imageSpinner = true;
    await this.handleGetImagesForAnnotation();
    await this.initializeModel();
    this.imageSpinner = false;
    await this.getAnnotatedObjectCount();
    this.taskObjectsList = this.taskObjects;
    this.interval = setInterval(this.getProcessProgress, 15000);
  },

  beforeUnmount() {
    clearInterval(this.interval);
  },

  unmounted() {
    this.resetAnnotationImagesList();
    this.annotatedObjectList = [];
    this.releaseModel();
  },

  methods: {
    ...mapActions([
      'getTaskObjects',
      'loadImagesForAnnotation',
      'setAnnotationImagesList',
      'setAnnotationImagesListCount',
      'setListLastImageObject',
      'resetAnnotationImagesList',
      'setTaskStaticImage',
      'updateTaskObject',
    ]),

    setFastAnnotation(value) {
      this.fastAnnotation = value;
    },

    preserveMaskDataInput(maskData) {
      this.preserveMaskData = maskData;
    },

    async handleSegmentPropagation() {
      const annotations = Object.keys(this.selectImage)
        .filter((key) => this.selectImage[key])
        .reduce((acc, key) => {
          const image = this.imagesForAnnotation.find((item) => item.id == key);
          if (image) {
            acc[key] = {
              path_to_image: image.path_to_image,
              path_to_annotations: image.path_to_annotations,
            };
          }
          return acc;
        }, {});
      const segmentPropagation = JSON.parse(
        localStorage.getItem('segmentPropagation')
      );
      await this.startSegmentPropagationProcess(
        annotations,
        segmentPropagation
      );
    },

    getSegmentPropagationImageId() {
      const item = JSON.parse(localStorage.getItem('segmentPropagation'));
      if (
        item === null ||
        item === undefined ||
        item['selectedTask'] !== this.selectedTask
      )
        return null;
      return item['annotationId'];
    },

    async startSegmentPropagationProcess(annotations, segmentPropagation) {
      let payload = {
        name: 'segment_propagation',
        status: longRunningTaskStatus.PENDING,
        details: {
          progress: 0,
        },
        event: {
          annotation_ids: Object.keys(annotations),
        },
        organization: this.organization,
        task: this.selectedTask,
      };
      const longRunningTaskId = await this.createLongRunningTaskId(payload);
      if (!longRunningTaskId)
        return this.toast.error('Failed to start segment propagation process.');
      payload = {
        queueName: ODQueues.ODGpuSmall,
        sqsMessage: {
          organization: this.organization,
          task_name: this.taskName,
          annotations: annotations,
          labels_coordinates: segmentPropagation['labels_coordinates'],
          message_type: 'segment_propagation',
          long_running_task_id: longRunningTaskId,
        },
      };
      await SQSServices.sendSQSMessage(payload);
      this.toast.info('Segment propagation process started!');
      await this.getProcessProgress();
    },

    revertObjectsProcess(status, data = null) {
      this.imageSpinner = status;
      if (status || !data) return;
      const tempAnnotationMap = new Map(
        this.imagesForAnnotation.map((image) => [image.id, { ...image }])
      );
      const imageFileMap = new Map(
        this.imageFileList.map((image) => [image.id, image])
      );

      data.forEach(({ id, task_object }) => {
        if (tempAnnotationMap.has(id)) {
          tempAnnotationMap.get(id).task_object = task_object;
        }
        if (imageFileMap.has(id)) {
          imageFileMap.get(id).task_object = task_object;
        }
      });

      this.setAnnotationImagesList(Array.from(tempAnnotationMap.values()));
    },

    isObjectEmpty(obj) {
      if (typeof obj === 'string') {
        obj = JSON.parse(obj);
      }
      if (obj && typeof obj === 'object') {
        // Check for array or regular object
        if (Array.isArray(obj)) {
          return obj.length === 0; // Empty if array has no elements
        } else {
          return Object.keys(obj).length === 0; // Empty if object has no keys
        }
      }
      return false; // Not an object or null/undefined
    },
    getRibbonColor(item) {
      // Check the conditions and return the appropriate color
      if (item.path_to_annotations && !this.isObjectEmpty(item.task_object)) {
        return colorPalette.green;
      } else if (
        item.path_to_annotations &&
        this.isObjectEmpty(item.task_object)
      ) {
        return colorPalette.yellow;
      } else if (!item.path_to_annotations) {
        return colorPalette.red;
      } else if (item.ai_annotation) {
        return colorPalette.orange;
      }
    },

    getRibbonText(item) {
      // Check the conditions and return the appropriate color
      if (item.path_to_annotations && !this.isObjectEmpty(item.task_object)) {
        return 'Annotated';
      } else if (
        item.path_to_annotations &&
        this.isObjectEmpty(item.task_object)
      ) {
        return 'Empty Annotated';
      } else if (!item.path_to_annotations) {
        return 'Not Annotated';
      }
    },

    populateMetaFile(item) {
      if (this.metaFiles) {
        const frameIndex = this.metaFiles.indexOf(item.path_to_image);
        if (frameIndex !== -1) {
          this.metaFiles.splice(frameIndex, 1); // Remove the existing frame
        } else {
          this.metaFiles.push(item.path_to_image);
        }
      } else {
        this.metaFiles.push(item.path_to_image);
      }
    },

    updateSelectItem(item) {
      this.populateMetaFile(item);
      if (!this.selectImage[item.id]) {
        this.selectImage[item.id] = true;
        return;
      }
      this.selectImage[item.id] = !this.selectImage[item.id];
    },

    async removeStaticImageCoordinates() {
      const objectList = this.getObjectListFromState();
      if (!objectList?.length) return;
      const promises = objectList.map((item) => {
        let payload = {
          objectId: item.id,
          objectData: {
            ...item,
            cordinates_of_static_object: null,
          },
        };
        return this.updateTaskObject(payload);
      });

      await Promise.all(promises);
    },

    getObjectListFromState() {
      const new_regions = this.getNewlyAddedObjs().map((o) => {
        return {
          ...o,
          cordinates_of_static_object: parseStringCoordsToObj(
            o.cordinates_of_static_object
          ),
          actual_name: o.name,
        };
      });

      let k = this.taskStaticObjectsList
        .filter((t) => t.step_id === null)
        .map((o) => {
          return {
            ...o,
            cordinates_of_static_object: parseStringCoordsToObj(
              o.cordinates_of_static_object
            ),
            actual_name: o.name,
          };
        });

      k = k.concat(new_regions);
      return k;
    },

    getNewlyAddedObjs() {
      return this.taskObjects.filter((t) => {
        let obj_exists = false;
        this.taskStaticObjectsList.filter((o) => {
          if (o.name === t.name) {
            obj_exists = true;
          }
        });
        return !obj_exists && t.is_static;
      });
    },

    async handleMarkStaticChanged(checked, event) {
      const payload = {
        annotation_image_id: checked ? this.markStaticImageId : null,
      };
      let [error, data] = await TaskService.updateTask(
        this.selectedTask,
        payload
      );
      if (error) return;
      this.setTaskStaticImage(payload.annotation_image_id);
      this.updateStaticImageList();
      await this.removeStaticImageCoordinates();
    },

    updateStaticImageList() {
      const keys = Object.keys(this.staticImage);
      keys.forEach((key) => {
        if (key === this.taskStaticAnnotationImage) {
          this.staticImage[key] = true;
        } else this.staticImage[key] = false;
      });
    },

    getImagetItemIndex(item) {
      return this.imagesForAnnotation.findIndex(
        (value) => value.id === item.id
      );
    },

    async handlePageChange(page) {
      this.currentPageForImage = page;
      await this.updateImageFileList();
    },

    async handlePageSizeChange(current, pageSize) {
      this.pageSizeForImage = pageSize;
      this.currentPageForImage = current;
      await this.updateImageFileList();
    },

    debouncedSearch: debounce(async function () {
      this.searchDebounce = this.searchImage;
      this.currentPageForImage = 1;
      await this.updateImageFileList();
    }, 500),

    getMinMaxIndex(search_image_list) {
      let minIndex, maxIndex;

      // If only one object, adjust min and max accordingly
      if (search_image_list.length === 1) {
        const singleIndex = search_image_list[0].index;
        minIndex = Math.max(0, singleIndex - 15); // Ensure min is not less than 0
        maxIndex = Math.min(singleIndex + 15, this.imagesForAnnotation.length); // Ensure max is not greater than this.imagesForAnnotation length
      } else {
        // Find min and max indices from the list
        minIndex = Math.min(...search_image_list.map((img) => img.index));
        maxIndex = Math.max(...search_image_list.map((img) => img.index)) + 1;
      }

      return { minIndex, maxIndex };
    },

    async updateImageFileList() {
      let temp_image = [...this.imagesForAnnotation];
      temp_image = this.getSearchedFiles(temp_image);
      this.totalImagesCount = temp_image.length;
      this.imageFileList = temp_image;
      if (!this.totalImagesCount) return;
      try {
        if (this.searchImage) {
          this.assignImageUrl = true;
          const { minIndex, maxIndex } = this.getMinMaxIndex(temp_image);
          this.presignedUrlList = await this.generateBulkImageS3Url(
            minIndex,
            maxIndex,
            this.organization,
            this.selectedTask
          );
          this.startIndexCache = minIndex;
          this.endIndexCache = maxIndex;
          return;
        } else {
          this.startIndexCache =
            (this.currentPageForImage - 1) * this.pageSizeForImage;
          this.endIndexCache = this.startIndexCache + this.pageSizeForImage;
        }
        if (this.endIndexCache >= temp_image.length)
          this.endIndexCache = temp_image.length;
        if (
          !this.searchDebounce &&
          (!this.presignedUrlList?.image?.[this.startIndexCache] ||
            !this.presignedUrlList?.image?.[this.endIndexCache - 1])
        ) {
          this.assignImageUrl = true;
          this.presignedUrlList = await this.generateBulkImageS3Url(
            this.startIndexCache,
            this.endIndexCache,
            this.organization,
            this.selectedTask
          );
        }

        if (!this.assignImageUrl) return;

        const indexMap = new Map(
          this.imageFileList.map((item, index) => [+item.index, index])
        );

        Object.entries(this.presignedUrlList?.image).forEach(([key, value]) => {
          const elementExist = indexMap.has(+key);
          if (elementExist) {
            this.imageFileList[indexMap.get(+key)].url = value;
          }
        });

        let tempAnnotaionImageList = [...this.imagesForAnnotation];
        indexMap.forEach((value, key) => {
          tempAnnotaionImageList[key].url = this.imageFileList[value].url;
        });

        this.setAnnotationImagesList(tempAnnotaionImageList);
        this.assignImageUrl = false;
      } catch (err) {
        console.error(err);
      }
    },

    getSearchedFiles(imageList) {
      const searchLowerCase = this.searchDebounce.toLowerCase().trim();
      const imageListWithIndex = imageList.map((image, index) => ({
        ...image,
        index: index, // Add the index attribute here
      }));

      return imageListWithIndex.filter((image) =>
        this.getFileName(image.path_to_image)
          .toLowerCase()
          .includes(searchLowerCase)
      );
    },

    handleSelectAllFiles(value) {
      for (let file of this.imageFileList) {
        this.selectImage[file.id] = this.selectAllImages;
        this.populateMetaFile(file);
      }
    },

    populateMetaFile(item) {
      if (this.metaFiles) {
        const frameIndex = this.metaFiles.indexOf(item.path_to_image);
        if (frameIndex !== -1) {
          this.metaFiles.splice(frameIndex, 1); // Remove the existing frame
        } else {
          this.metaFiles.push(item.path_to_image);
        }
      } else {
        this.metaFiles.push(item.path_to_image);
      }
    },

    async handleDelete() {
      let keys = Object.keys(this.selectImage).filter(
        (key) => this.selectImage[key] === true
      );
      if (!keys.length) return;
      this.isBulkDelete = true;
      try {
        const [deleteResult] = await Promise.all([
          TaskObjectService.bulkDeleteAnnotations({ ids: keys }, true),
          this.updateMetaFiles(this.taskName, this.metaFiles),
        ]);

        const [error, data] = deleteResult;

        if (error) {
          await this.handleGetImagesForAnnotation();
          return this.toast.error('Failed to delete files.');
        }
        let msgSuccess = true;
        if (data?.failed_files?.length) {
          msgSuccess = false;
          for (let file of data.failed_files) {
            keys = keys.filter((item) => item != file);
          }
        }
        keys.forEach((key) => this.updateImageList(key));
        this.updateTaskStaticImageAndCoordinates(keys);
        this.presignedUrlList = {};
        // this.currentPageForImage = 1;
        await this.updateImageFileList();
        return msgSuccess
          ? this.toast.success(data.message)
          : this.toast.error(data.message);
      } catch (err) {
        this.toast.error('Unable to delete some files.');
      } finally {
        this.isBulkDelete = false;
        this.metaFiles = [];
      }
    },

    async handleDeleteAnnotations() {
      let keys = Object.keys(this.selectImage).filter(
        (key) => this.selectImage[key] === true
      );
      if (!keys.length) return;
      this.isBulkDelete = true;
      try {
        const [error, data] = TaskObjectService.deleteAnnotations(
          { ids: keys },
          true
        );

        if (error) {
          return this.toast.error('Failed to delete Annotations');
        }
        this.toast.success(data.message);
      } finally {
        this.selectImage = {};
        await this.handleGetImagesForAnnotation();
        await this.getAnnotatedObjectCount();
        this.isBulkDelete = false;
        this.metaFiles = [];
      }
    },

    async markImagesAsReviewed() {
      let keys = Object.keys(this.selectImage).filter(
        (key) => this.selectImage[key] === true
      );
      if (!keys.length) return;
      try {
        const [error, data] = await TaskObjectService.markReviewedFrames(
          { ids: keys },
          true
        );
        if (error) {
          return this.toast.error('Failed to mark frames reviewed');
        }
        this.toast.success(data.message);
      } finally {
        this.selectImage = {};
        await this.handleGetImagesForAnnotation();
        this.metaFiles = [];
      }
    },

    getPreMetaDataUrl(file_name) {
      const organization = localStorage.getItem('organization');
      const url = `organization/task/upload/retrieve_metadata?org=${organization}&task_name=${this.taskName}&video_name=${file_name}&task_id=${this.selectedTask}`;
      return url;
    },
    async getPrevMetaData(file_name) {
      const url = this.getPreMetaDataUrl(file_name);
      let response = await httpClient.getData(url, false);
      return response;
    },
    saveMetaFile(json, meta_file_name) {
      return new Promise(async (resolve, _) => {
        var blob = new Blob([JSON.stringify(json)], {
          type: 'text/json;charset=utf-8',
        });
        var formData = new FormData();
        formData.append('file', blob, meta_file_name + '.json');
        formData.append('task_id', this.selectedTask);
        formData.append('Validation', 'False');
        const response = await httpClient.upload(
          'organization/task/upload/',
          formData,
          this
        );
        resolve(response);
      });
    },

    updateTaskStaticImageAndCoordinates(keys) {
      const staticImage = keys.filter(
        (item) => item == this.taskStaticAnnotationImage
      );

      if (staticImage.length) {
        this.resetStaticImageAndCoordinates();
      }
    },

    resetStaticImageAndCoordinates() {
      if (!this.taskStaticAnnotationImage) return;
      this.staticImage[this.taskStaticAnnotationImage] = false;
      this.setTaskStaticImage(null);
      // await this.removeStaticImageCoordinates();
    },

    resetSelection() {
      this.selectAllImages = false;
      this.selectImage = {};
    },

    addItemInImageList(file) {
      let tempAnnotation = this.imagesForAnnotation;
      tempAnnotation.push(file);
      this.setAnnotationImagesList(tempAnnotation);
      this.setAnnotationImagesListCount(tempAnnotation.length);
    },

    updateSelectAll(obj) {
      this.selectImage[obj.id] = obj.selected;
    },

    handleChangeView() {
      this.listView = !this.listView;
    },

    getActiveUserRole() {
      // Store getter, not localStorage, so "View as customer" applies.
      const user = this.$store.getters.role;
      if (user === roles.support_user) this.userRole = true;
      else this.userRole = false;
    },

    handleUploadFile(info) {
      const isZip = info.file.name.endsWith('.zip');
      const maxSizeInBytes = 1 * 1024 * 1024 * 1024; // 1 GB
      if (!isZip) {
        this.toast.error('Please upload .zip files only!');
        this.fileList = [];
        return;
      }
      if (info.file.size > maxSizeInBytes) {
        this.toast.error('File size must be less than 1 GB!');
        this.fileList = [];
        return;
      }
    },

    async reloadImagesAfterUpload() {
      await this.reloadImages();
      this.currentPageForImage = 1;
    },

    async uploadFileOnS3(file) {
      const s3Key = `${this.taskName}/compressedImages/${file.name}`;
      const response = await UploadFileHelper.getProjectorFileUploadURL(s3Key);
      if (!response?.url) {
        return false;
      }
      try {
        const payload = UploadFileHelper.createAWSFormData(file, response);
        const headers = {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        };
        await axios.post(response.url, payload, {
          headers,
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            let percent = Math.floor((loaded * 100) / total);
            if (percent <= 100) {
              this.fileUploadStatus = percent < 100 ? this.activeStatus : '';
              this.uploadingZipDetails = percent;
            }
          },
        });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    async handleUploadImageFile() {
      if (this.fileUploadProgress === 100) {
        await this.handleCancel();
        return;
      }
      this.isUploadingFile = true;
      const file = this.fileList[0].originFileObj;
      const error = await this.uploadFileOnS3(file);
      if (!error) {
        this.isUploadingFile = false;
        this.toast.error('Failed to upload file!');
        return;
      }
      const payload = {
        task_id: this.selectedTask,
        name: 'decompress_annotation_images',
        organization: this.organization,
        status: 'started',
      };
      this.longRunningTaskId = await this.createLongRunningTaskId(payload);
      if (!this.longRunningTaskId) {
        this.handleCancel();
        this.toast.error('Failed to unzip files!');
        return;
      }
      const response = await this.sendMessageToQueue(file.name);
      if (!response) {
        this.handleCancel();
        this.toast.error('Failed to unzip files!');
        return;
      }
      this.toast.info('Please wait while we unzip images.');
      this.pollImageStatus();
    },

    async createLongRunningTaskId(payload) {
      const [error, data] = await LRTService.createLongRunningTasks(
        payload,
        true
      );
      if (error) return null;
      return data.id;
    },

    async sendMessageToQueue(fileName) {
      const payload = {
        queueName: ODQueues.DecompressODImagesDevJobs,
        sqsMessage: {
          task_id: this.selectedTask,
          task_name: this.taskName,
          organization: this.organization,
          file_name: fileName,
          long_running_task_id: this.longRunningTaskId,
          is_annotation_files: this.isAnnotationFiles,
        },
      };

      const [error] = await SQSServices.sendSQSMessage(payload);
      if (error) {
        console.error('error:', error);
        return false;
      }
      return true;
    },

    async handleCancelUploadModal() {
      if (this.isUploadingFile) return this.toast.info('Please wait...');
      await this.handleCancel();
    },

    async handleCancel() {
      this.fileUpload = false;
      this.isUploadingFile = false;
      this.fileList = [];
      this.failedFiles = [];
      this.uploadingZipDetails = 0;
      this.isAnnotationFiles = false;
      this.fileUploadStatus = this.activeStatus;
      if (this.fileUploadProgress === 100) await this.reloadImagesAfterUpload();
      this.fileUploadProgress = 0;
    },

    pollImageStatus() {
      this.fileUploadStatus = this.activeStatus;
      this.initializeTimeout();
      this.startPolling();
    },

    initializeTimeout() {
      this.timeout = setTimeout(async () => {
        if (this.polling === null) return;
        this.toast.error('Failed to unzip images. Timeout occured!');
        this.resetPolling();
        await this.handleCancel();
        await this.reloadImagesAfterUpload();
      }, 900000); // 15 mins
    },

    resetTimeout() {
      clearTimeout(this.timeout);
      this.timeout = null;
    },

    startPolling() {
      this.polling = setInterval(this.fetchImageStatus, 3000);
    },

    resetPolling() {
      clearInterval(this.polling);
      this.polling = null;
      this.longRunningTaskId = null;
      this.resetTimeout();
    },

    async fetchImageStatus() {
      if (this.isPolling) return;
      this.isPolling = true;
      try {
        const [error, data] = await LRTService.getLongRunningTaskById(
          this.longRunningTaskId,
          false
        );
        if (error) {
          this.toast.error('Failed to unzip images!');
          await this.handleCancel();
          this.resetPolling();
          return;
        }
        if (data?.details?.progress) {
          this.uploadingZipDetails = 0;
          this.fileUploadStatus = this.activeStatus;
          this.fileUploadProgress = Math.floor(data.details.progress);
          if (this.fileUploadProgress === 100) {
            this.fileUploadStatus = '';
            this.isUploadingFile = false;
          }
        }
        if (data?.status === 'finished') {
          this.resetPolling();
          if (data?.details?.files_not_uploaded?.length) {
            this.failedFiles = data?.details?.files_not_uploaded;
          } else {
            await this.handleCancel();
          }
        }
        if (data?.error_field) {
          this.toast.error('Failed to unzip images!');
          await this.handleCancel();
          this.resetPolling();
          return;
        }
      } finally {
        this.isPolling = false;
      }
    },

    currentImageIndexChanged(index) {
      if (index < 0 || index >= this.totalImagesForAnnotation) {
        this.loading = false;
      }
      this.selectedImage = this.imagesForAnnotation[index];
      this.openImageForAnnotation(this.imagesForAnnotation[index]);
      this.loading = false;
    },

    async getAnnotatedObjectCount() {
      await this.getTaskObjects(this.selectedTask);
      this.annotatedObjectList = [];
      this.taskObjects.forEach((obj) => {
        this.annotatedObjectList.push({
          name: obj.name,
          count: obj.number_of_examples,
          is_static: obj.is_static,
        });
      });
    },

    async handleGetImagesForAnnotation() {
      const payload = {
        task: this.selectedTask,
        filter: this.annotationFilterValues,
        page: this.currentPageForImage,
        pageSize: this.pageSizeForImage,
        ordering: this.sortingValue,
      };
      await this.loadImagesForAnnotation(payload).then(async () => {
        this.presignedUrlList = {};
        await this.updateImageFileList();
      });
    },

    async handleSortImagesForAnnotation(sortingValue) {
      if (this.sortingValue != sortingValue) {
        this.sortingValue = sortingValue;
        const payload = {
          task: this.selectedTask,
          filter: this.annotationFilterValues,
          page: this.currentPageForImage,
          pageSize: this.pageSizeForImage,
          ordering: this.sortingValue,
        };
        await this.loadImagesForAnnotation(payload).then(async () => {
          this.presignedUrlList = {};
          await this.updateImageFileList();
        });
      }
    },

    async deleteImageAnnotation(id) {
      const res = await httpClient.delete(
        `organization/task/annotation_image/`,
        id + '/',
        false
      );
      if (res === 'error') {
        this.toast.error('error in getting images');
        return;
      }
      delete this.selectImage[id];
      if (!this.listView) return await this.handleGetImagesForAnnotation();
      this.updateImageList(id);
    },

    updateImageList(id) {
      const tempAnnotation = this.imagesForAnnotation.filter(
        (item) => item.id != id
      );
      this.setAnnotationImagesList(tempAnnotation);
      this.setAnnotationImagesListCount(tempAnnotation.length);
    },

    async generateBulkImageS3Url(startIndex, endIndex, organization, taskId) {
      const obj = {
        bucket_name: organization + '-training',
        task_id: taskId,
        start_index: startIndex,
        end_index: endIndex,
        filter: this.annotationFilterValues,
        ordering: this.sortingValue,
        get_embeddings: true,
      };
      try {
        const response = await httpClient.post(
          'generic/generate_list_new_url/',
          obj,
          false,
          false,
          false
        );
        const presigned_url = response.presigned_url;
        return presigned_url;
      } catch (error) {
        console.error('Error generating presigned URL ' + error, 3000);
      }
    },
    async updateMetaFiles(taskName, metaFiles) {
      const urlForTaskProgress = `organization/annotations/update-meta-files/?task=${taskName}`;
      let res = await httpClient.patch(
        urlForTaskProgress,
        '',
        { images: metaFiles, task_id: this.selectedTask },
        false,
        false,
        false
      );
      return res[0];
    },
    async reloadImages() {
      this.selectImage = {};
      this.presignedUrlList = {};
      await this.handleGetImagesForAnnotation();
    },

    updateCacheIndex(startIndex, endIndex) {
      this.startIndexCache = startIndex;
      this.endIndexCache = endIndex;
    },

    handleImageChange(newIndex) {
      this.currentImageIndex += newIndex;
      this.currentPageForImage =
        Math.floor(this.currentImageIndex / this.pageSizeForImage) + 1;
      this.currentImageIndexChanged(this.currentImageIndex);
    },

    async openAnnotationModal(item) {
      const index = this.getImagetItemIndex(item);
      if (index === -1)
        return this.toast.error('Failed to open Annotation tool!');
      this.selectedImage = this.imagesForAnnotation[index];
      this.currentImageIndex = index;
      this.loading = false;
      this.currentImageIndexChanged(this.currentImageIndex);
      this.searchImage = '';
      this.searchDebounce = '';

      if (!this.presignedUrlList.hasOwnProperty('image')) return;
      const presigned_keys = Object.keys(this.presignedUrlList.image);
      const presigned_url_length = presigned_keys.length;
      if (presigned_url_length === 0) return;
      const last_presigned_index = presigned_keys[presigned_url_length - 1];
      const first_presigned_index = presigned_keys[presigned_url_length - 1];
      if (last_presigned_index < index || first_presigned_index > index) {
        this.startIndexCache = Math.max(0, index - 15);
        this.endIndexCache = Math.min(
          index + 15,
          this.imagesForAnnotation.length
        );
      }
    },

    getCurrentIndex(index) {
      return (this.currentPageForImage - 1) * this.pageSizeForImage + index;
    },

    setImageData(image) {
      if (!image) return;
      this.requestedImagePath = image.path_to_image;
      this.requestedImageId = image.id;
    },

    async deleteAnnotation(index, item) {
      await this.deleteImageAnnotation(item['id']);
    },

    openImageForAnnotation(image) {
      this.setImageData(image);
      this.openObjectAnnotateDialog = true;
      this.startTime = new Date().toISOString();
    },

    updateAnnotationPath(updatedAnnotation = {}) {
      const tempAnnotation = [...this.imagesForAnnotation];
      let currentAnnotation = tempAnnotation[this.currentImageIndex];
      for (const [key, value] of Object.entries(updatedAnnotation)) {
        currentAnnotation[key] = value;
      }

      this.setAnnotationImagesList(tempAnnotation);
    },

    getFileName(name) {
      if (name) return name.split('/').at(-1);
    },

    setShowAiAnnotation(value) {
      this.showAiAnnotation = value;
    },
    setShowRevertObjects(value) {
      this.showRevertObjects = value;
    },
    handleAiAnnotation() {
      this.setShowAiAnnotation(true);
    },
    handleShowRevertObject() {
      this.setShowRevertObjects(true);
    },

    async getAiAnnotationProgress() {
      const taskFilter = {
        name: 'auto annotation',
        task: this.selectedTask,
      };
      const [errorStarted, started] = await LRTService.getLongRunningTasks(
        false,
        {
          ...taskFilter,
          status: longRunningTaskStatus.STARTED,
        }
      );

      const [errorPending, pending] = await LRTService.getLongRunningTasks(
        false,
        {
          ...taskFilter,
          status: longRunningTaskStatus.PENDING,
        }
      );
      if (errorStarted && errorPending) return [];
      if (!errorStarted && errorPending) return [started, null];
      if (errorStarted && !errorPending) return [null, pending];
      else return [started, pending];
    },

    async getSegmentPropagationProgress() {
      const taskFilter = {
        name: 'segment_propagation',
        task: this.selectedTask,
      };
      const [errorStarted, started] = await LRTService.getLongRunningTasks(
        false,
        {
          ...taskFilter,
          status: longRunningTaskStatus.STARTED,
        }
      );
      const [errorPending, pending] = await LRTService.getLongRunningTasks(
        false,
        {
          ...taskFilter,
          status: longRunningTaskStatus.PENDING,
        }
      );
      if (errorStarted && errorPending) return [];
      if (!errorStarted && errorPending) return [started, null];
      if (errorStarted && !errorPending) return [null, pending];
      else return [started, pending];
    },

    async getProcessProgress() {
      const autoAnnotationProgress = await this.getAiAnnotationProgress();
      const segmentPropagationProgress =
        await this.getSegmentPropagationProgress();

      let annotationStarted = [];
      let annotationPending = [];
      let segmentPropagationStarted = [];
      let segmentPropagationPending = [];

      if (autoAnnotationProgress.length) {
        if (autoAnnotationProgress[0] != null)
          annotationStarted = autoAnnotationProgress[0];
        if (autoAnnotationProgress[1] != null)
          annotationPending = autoAnnotationProgress[1];
      }

      if (segmentPropagationProgress.length) {
        if (segmentPropagationProgress[0] != null)
          segmentPropagationStarted = segmentPropagationProgress[0];
        if (segmentPropagationProgress[1] != null)
          segmentPropagationPending = segmentPropagationProgress[1];
      }

      const startedProgress = [
        ...annotationStarted,
        ...segmentPropagationStarted,
      ];

      const pendingProgress = [
        ...annotationPending,
        ...segmentPropagationPending,
      ];

      this.annotationProgress = [];
      this.getStartedProgress(startedProgress);
      this.getPendingProgress(pendingProgress);
      this.getCompletedAnnotations(startedProgress);
    },

    getCompletedAnnotations(started) {
      started.forEach(async (item) => {
        if (!this.currentAiAnnotationSet.has(item.id))
          this.currentAiAnnotationSet.add(item.id);
        else await this.reloadImageIfAnnotated(item.id);
      });

      if (!started.length) {
        this.currentAiAnnotationSet.forEach(async (id) => {
          await this.reloadImageIfAnnotated(id);
        });
        this.currentAiAnnotationSet.clear();
      }
    },

    getStartedProgress(started) {
      started.forEach((item) => {
        this.progressProcessType = 'AI Segment Propagation';
        let name = 'Segment Propagation';
        if (item.event?.model_version) {
          name = 'AI Annotation - Model Version ' + item.event?.model_version;
          this.progressProcessType = 'AI Annotation';
        }
        const payload = {
          id: item.id,
          name: name,
          status: longRunningTaskStatus.STARTED,
          progress: item.details.progress,
        };
        this.annotationProgress.push(payload);
      });
    },

    getPendingProgress(pending) {
      pending.forEach((item) => {
        this.progressProcessType = 'AI Segment Propagation';
        let name = 'Segment Propagation';
        if (item.event?.model_version) {
          name = 'AI Annotation - Model Version ' + item.event?.model_version;
          this.progressProcessType = 'AI Annotation';
        }
        const payload = {
          id: item.id,
          name: name,
          status: 'Queue',
        };
        this.annotationProgress.push(payload);
      });
    },

    async reloadImageIfAnnotated(id) {
      const [_, data] = await LRTService.getLongRunningTaskById(id, false);
      if (data?.status === 'finished') {
        let annotationIds = [];
        if (
          Object.prototype.hasOwnProperty.call(data.details, 'annotation_ids')
        )
          annotationIds = data.details.annotation_ids;
        else if (
          Object.prototype.hasOwnProperty.call(data.event, 'annotation_ids')
        )
          annotationIds = data.event.annotation_ids;
        this.currentAiAnnotationSet.delete(id);
        await this.updateAnnotationList({
          annotation_ids: annotationIds,
        });
      }
    },

    async updateAnnotationList(payload) {
      try {
        this.reloadPresignedUrl = false;
        const [error, data] = await TaskObjectService.fetchAnnotationsByIds(
          payload
        );
        if (error)
          return this.toast.error('Failed to fetch updated annotations.');

        let tempAnnotaionImageList = [...this.imagesForAnnotation];

        data.forEach((item) => {
          const index = tempAnnotaionImageList.findIndex(
            (object) => object.id === item.id
          );
          if (index) tempAnnotaionImageList[index] = item;
        });

        this.setAnnotationImagesList(tempAnnotaionImageList);
        this.imageFileList = [...tempAnnotaionImageList];
        this.presignedUrlList = await this.generateBulkImageS3Url(
          this.startIndexCache,
          this.endIndexCache,
          this.organization,
          this.selectedTask
        );
        this.reloadPresignedUrl = true;
      } catch (error) {
        console.error(error);
      }
    },

    setShowAiAnnotationResults(value) {
      this.showAiAnnotationResults = value;
    },

    handleAiAnnotationResultsClick() {
      this.setShowAiAnnotationResults(true);
    },

    releaseModel() {
      if (this.model) this.model?.release();
      this.model = null;
    },

    async initializeModel() {
      try {
        if (this.model === null) {
          const modelPath = '/model/sam2.1_hiera_tiny_decoder.onnx';
          const provider = navigator.gpu ? 'webgpu' : 'wasm';
          this.model = await ort.InferenceSession.create(modelPath, {
            executionProviders: [provider],
          });
        }
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style scoped>
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 75vh; /* Ensures spinner is centered vertically within the viewport */
}
</style>

<style scoped>
#components-grid-demo-playground pre {
  background: #f9f9f9;
  border-radius: 6px;
  font-size: 13px;
  padding: 8px 16px;
}
</style>

<style scoped>
/* Ensures the info icon is visible & aligned */
.info-icon {
  margin-left: 5px;
  font-size: 16px;
  color: #1890ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

/* Formats the popover content properly */
.popover-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
</style>

<style>
#object-detection-pagination
  > li:last-child
  .ant-pagination-options-size-changer.ant-select {
  width: 90px !important;
}
.grid-icon-btn {
  border-color: #d9d9d9;
}
.grid-icon-btn svg {
  width: 14px;
  height: 14px;
}
#label-objects-list .ant-list-item {
  display: flex;
  justify-content: center;
}

#mark-objects-card > .ant-card-body {
  padding: 0px !important;
}

.selected-item {
  background-color: lightgray;
}

.file-item:hover {
  background-color: #f5f5f5; /* Very light gray for hover effect */
}

.list-pagination-display .ant-list-pagination {
  display: none;
}
</style>
